/**
 * The Pomodoro focus timer as a pure reducer.
 *
 * Time is never ticked into state. A running block stores WHEN it started
 * (plus how long it has spent paused) and everything else - time left, whether
 * the block is over - is derived from `now`. So a refresh, a route change, a
 * second tab or a laptop that slept through the end of a block all read the
 * same answer, and `tick` is just "re-derive and apply any transition that is
 * now due".
 *
 * Phases: `idle → focus ⇄ paused → break → idle`. The break starts by itself
 * the moment a focus block ends, so a student who ignores the timer is never
 * stuck; the NEXT focus block never starts by itself, so nobody is pushed
 * into another round.
 *
 * A SESSION is a planned number of rounds - a focus block each, with a break
 * between them - from Start to the last round, or to "I'm done" if that comes
 * first. Ending one records `lastSession`, which the Focus page turns into a
 * summary. Between rounds, once a break has run out, the session waits idle
 * for the next Start: it never starts a round by itself.
 */

/** `timed` counts down from `focusMinutes`; `flow` counts up until stopped. */
export type FocusMode = 'timed' | 'flow';
/**
 * How the session is seen: the classic ring, or the sakura that blooms as you
 * focus. Chosen at set-up and fixed for the session.
 */
export type FocusView = 'classic' | 'sakura';
export type FocusPhase = 'idle' | 'focus' | 'paused' | 'break';
export type BreakKind = 'short' | 'long';

import {
  AWAY_THRESHOLD_MS,
  autoLongBreak,
  autoShortBreak,
  clamp,
  DEFAULT_FOCUS_MINUTES,
  DEFAULT_ROUNDS,
  flowBreakMs,
  longBreakAfter,
  MIN_CREDIT_MS,
  MINUTE,
  PAUSE_LIMIT_MS,
  sessionPlan,
  type SessionPlan,
} from './sessionPlan';

export * from './sessionPlan';

export interface ParkingNote {
  id: string;
  text: string;
}

/** The focus block that most recently ended - what the break is about. */
export interface FocusBlock {
  id: string;
  startedAt: number;
  endedAt: number;
  focusMs: number;
  endedEarly: boolean;
  /** How long ago the block ended when the app noticed (0 if it was open). */
  awayMs: number;
  /** What the block was for: free text, or a lesson's title. */
  intention?: string;
  lessonId?: string;
  recap?: string;
  recapDismissed?: boolean;
}

/** A finished session, for the summary. Blocks themselves are in the log. */
export interface FocusSession {
  startedAt: number;
  endedAt: number;
  blocks: number;
  parked: number;
  /**
   * The thoughts still parked when the session ended, so the summary can
   * hand them back. Optional: sessions stored before this existed have none.
   */
  notes?: ParkingNote[];
  /** The plan it was started with, and how it was seen (for the reward). */
  rounds?: number;
  view?: FocusView;
  seed?: number;
}

export interface FocusState {
  version: 2;
  mode: FocusMode;
  /** Length of a timed block. Ignored by Flow. */
  focusMinutes: number;
  /** The session's plan, fixed at Start (timed only). */
  rounds: number;
  shortMinutes: number;
  longMinutes: number;
  /** How this session is seen, and the seed its picture is drawn from. */
  view: FocusView;
  seed: number;
  phase: FocusPhase;
  /** Focus blocks finished in this session; drives the long break. */
  completedRounds: number;
  sessionStartedAt: number | null;
  startedAt: number | null;
  pausedAt: number | null;
  pausedMs: number;
  /** Length of the current focus block or break. `null` = Flow focus. */
  plannedMs: number | null;
  breakKind: BreakKind | null;
  intention?: string;
  lessonId?: string;
  parkingLot: ParkingNote[];
  /** Thoughts parked this session, including any since removed. */
  parkedCount: number;
  lastBlock: FocusBlock | null;
  lastSession: FocusSession | null;
}

export const INITIAL_FOCUS_STATE: FocusState = {
  version: 2,
  mode: 'timed',
  focusMinutes: DEFAULT_FOCUS_MINUTES,
  rounds: DEFAULT_ROUNDS,
  shortMinutes: autoShortBreak(DEFAULT_FOCUS_MINUTES),
  longMinutes: autoLongBreak(autoShortBreak(DEFAULT_FOCUS_MINUTES)),
  view: 'classic',
  seed: 0,
  phase: 'idle',
  completedRounds: 0,
  sessionStartedAt: null,
  startedAt: null,
  pausedAt: null,
  pausedMs: 0,
  plannedMs: null,
  breakKind: null,
  parkingLot: [],
  parkedCount: 0,
  lastBlock: null,
  lastSession: null,
};

/**
 * Read stored state from any build. Version 1 had fixed `classic` / `deep`
 * modes and a `lessonTitle`; both map onto the current shape, so a block
 * running across the upgrade keeps running.
 */
export function normalizeFocusState(stored: unknown): FocusState {
  const raw = (stored ?? {}) as Record<string, unknown>;
  const merged = { ...INITIAL_FOCUS_STATE, ...raw } as FocusState &
    Record<string, unknown>;
  if (raw.mode === 'classic' || raw.mode === 'deep') {
    merged.mode = 'timed';
    merged.focusMinutes = raw.mode === 'deep' ? 50 : 25;
  }
  if (!merged.intention && typeof raw.lessonTitle === 'string') {
    merged.intention = raw.lessonTitle;
  }
  delete merged.lessonTitle;
  merged.version = 2;
  return merged;
}

export type FocusAction =
  | {
      type: 'start';
      mode: FocusMode;
      focusMinutes: number;
      intention?: string;
      lessonId?: string;
      /** The plan; absent fields keep the current one (or follow auto). */
      rounds?: number;
      shortMinutes?: number | null;
      longMinutes?: number | null;
      view?: FocusView;
      seed?: number;
    }
  | { type: 'pause' }
  | { type: 'resume' }
  /** End the focus block now - early, or Flow's "I'm done". */
  | { type: 'finish' }
  | { type: 'tick' }
  /** Break (or idle) → a new focus block on the same settings. */
  | { type: 'nextBlock' }
  | { type: 'endBreak' }
  | { type: 'endSession' }
  /** The summary's parked thoughts were kept elsewhere (the Worry dump). */
  | { type: 'clearSessionNotes' }
  | { type: 'dismissSummary' }
  | { type: 'addNote'; text: string }
  | { type: 'removeNote'; id: string }
  | { type: 'saveRecap'; text: string }
  | { type: 'dismissRecap' };

/** Time spent in the current phase, excluding pauses. */
export function elapsedMs(state: FocusState, now: number): number {
  if (state.startedAt === null) return 0;
  const end = state.phase === 'paused' ? (state.pausedAt ?? now) : now;
  return Math.max(0, end - state.startedAt - state.pausedMs);
}

/** Time left in the current phase, or `null` when it counts up (Flow). */
export function remainingMs(state: FocusState, now: number): number | null {
  if (state.plannedMs === null) return null;
  return Math.max(0, state.plannedMs - elapsedMs(state, now));
}

/**
 * "Round 2 of 4": the block in progress, the one a break follows, or - idle
 * between rounds - the one about to start. `null` for Flow.
 */
export function roundInSet(
  state: FocusState
): { current: number; total: number } | null {
  if (state.mode === 'flow') return null;
  const total = state.rounds;
  const index =
    state.phase === 'break' ? state.completedRounds : state.completedRounds + 1;
  return { current: clamp(index, 1, total), total };
}

/** Idle with a session under way: a break ran out and the next round waits. */
export const isBetweenRounds = (state: FocusState) =>
  state.phase === 'idle' &&
  state.sessionStartedAt !== null &&
  state.completedRounds > 0;

function focusFields(
  state: Pick<FocusState, 'mode' | 'focusMinutes'>,
  now: number
): Pick<
  FocusState,
  'phase' | 'startedAt' | 'pausedAt' | 'pausedMs' | 'plannedMs' | 'breakKind'
> {
  return {
    phase: 'focus',
    startedAt: now,
    pausedAt: null,
    pausedMs: 0,
    plannedMs: state.mode === 'flow' ? null : state.focusMinutes * MINUTE,
    breakKind: null,
  };
}

function toIdle(state: FocusState): FocusState {
  return {
    ...state,
    phase: 'idle',
    startedAt: null,
    pausedAt: null,
    pausedMs: 0,
    plannedMs: null,
    breakKind: null,
  };
}

function breakFor(
  state: FocusState,
  completedRounds: number,
  focusMs: number
): { kind: BreakKind; ms: number } {
  if (state.mode === 'flow') return { kind: 'short', ms: flowBreakMs(focusMs) };
  return completedRounds === longBreakAfter(state.rounds)
    ? { kind: 'long', ms: state.longMinutes * MINUTE }
    : { kind: 'short', ms: state.shortMinutes * MINUTE };
}

/**
 * Close the focus block. `early` = the student (or the pause limit) ended it
 * before its planned length; otherwise it ran out, possibly while nobody was
 * looking, in which case it is credited its planned length and no more.
 */
function endFocus(state: FocusState, now: number, early: boolean): FocusState {
  if (state.startedAt === null) return toIdle(state);
  const elapsed = elapsedMs(state, now);
  const credited =
    state.plannedMs === null ? elapsed : Math.min(elapsed, state.plannedMs);
  if (credited < MIN_CREDIT_MS) return toIdle(state);

  const endedAt = early
    ? (state.pausedAt ?? now)
    : state.startedAt + state.pausedMs + credited;
  const lateBy = now - endedAt;
  const ranOut = !early && state.plannedMs !== null;
  const completedRounds = state.completedRounds + 1;
  const pause = breakFor(state, completedRounds, credited);

  const ended: FocusState = {
    ...state,
    phase: 'break',
    completedRounds,
    // The break is timed from when the block ENDED, so a block that ran out
    // while the laptop was shut has already eaten into its break.
    startedAt: endedAt,
    pausedAt: null,
    pausedMs: 0,
    plannedMs: pause.ms,
    breakKind: pause.kind,
    lastBlock: {
      id: `block-${state.startedAt}`,
      startedAt: state.startedAt,
      endedAt,
      focusMs: credited,
      endedEarly: early,
      // Only a block that RAN OUT can have ended unseen. Flow's "I'm done"
      // is not early, but it is always pressed by someone who is there.
      awayMs: ranOut && lateBy > AWAY_THRESHOLD_MS ? lateBy : 0,
      intention: state.intention,
      lessonId: state.lessonId,
    },
  };
  // The last planned round ends the session: straight to the summary, with
  // no break after it.
  const last = state.mode === 'timed' && completedRounds >= state.rounds;
  return last ? endSession(ended, endedAt) : ended;
}

function tick(state: FocusState, now: number): FocusState {
  if (state.phase === 'focus') {
    const left = remainingMs(state, now);
    return left === 0 ? endFocus(state, now, false) : state;
  }
  if (state.phase === 'paused' && state.pausedAt !== null) {
    return now - state.pausedAt >= PAUSE_LIMIT_MS
      ? endFocus(state, now, true)
      : state;
  }
  if (state.phase === 'break') {
    return remainingMs(state, now) === 0 ? toIdle(state) : state;
  }
  return state;
}

function updateBlock(
  state: FocusState,
  patch: Partial<FocusBlock>
): FocusState {
  if (!state.lastBlock) return state;
  return { ...state, lastBlock: { ...state.lastBlock, ...patch } };
}

function samePlan(state: FocusState, mode: FocusMode, plan: SessionPlan) {
  return (
    state.sessionStartedAt !== null &&
    mode === state.mode &&
    plan.focusMinutes === state.focusMinutes &&
    plan.rounds === state.rounds &&
    plan.shortMinutes === state.shortMinutes &&
    plan.longMinutes === state.longMinutes
  );
}

function startBlock(
  state: FocusState,
  action: Extract<FocusAction, { type: 'start' }>,
  now: number
): FocusState {
  if (state.phase === 'focus' || state.phase === 'paused') return state;
  const plan = sessionPlan({
    focusMinutes: action.focusMinutes,
    rounds: action.rounds ?? state.rounds,
    shortMinutes: action.shortMinutes,
    longMinutes: action.longMinutes,
  });
  // The same plan, mid-session: the round count carries on, on the same
  // view. Anything else is a fresh session.
  const carried = samePlan(state, action.mode, plan)
    ? {
        view: state.view,
        seed: state.seed,
        completedRounds: state.completedRounds,
        sessionStartedAt: state.sessionStartedAt,
        parkedCount: state.parkedCount,
      }
    : {
        view: action.view ?? 'classic',
        seed: action.seed ?? now,
        completedRounds: 0,
        sessionStartedAt: now,
        parkedCount: 0,
      };
  const next: FocusState = {
    ...state,
    ...carried,
    mode: action.mode,
    focusMinutes: plan.focusMinutes,
    rounds: plan.rounds,
    shortMinutes: plan.shortMinutes,
    longMinutes: plan.longMinutes,
    intention: action.intention?.trim() || undefined,
    lessonId: action.lessonId,
    lastSession: null,
  };
  return { ...next, ...focusFields(next, now) };
}

function endSession(state: FocusState, now: number): FocusState {
  const lastSession: FocusSession | null =
    state.sessionStartedAt !== null && state.completedRounds > 0
      ? {
          startedAt: state.sessionStartedAt,
          endedAt: now,
          blocks: state.completedRounds,
          parked: state.parkedCount,
          notes: state.parkingLot,
          rounds: state.mode === 'timed' ? state.rounds : undefined,
          view: state.view,
          seed: state.seed,
        }
      : null;
  return {
    ...toIdle(state),
    completedRounds: 0,
    sessionStartedAt: null,
    parkingLot: [],
    parkedCount: 0,
    lastSession,
  };
}

function resume(state: FocusState, now: number): FocusState {
  if (state.phase !== 'paused' || state.pausedAt === null) return state;
  return {
    ...state,
    phase: 'focus',
    pausedMs: state.pausedMs + (now - state.pausedAt),
    pausedAt: null,
  };
}

function addNote(state: FocusState, text: string, now: number): FocusState {
  const trimmed = text.trim();
  if (!trimmed) return state;
  const note = { id: `note-${now}-${state.parkingLot.length}`, text: trimmed };
  return {
    ...state,
    parkingLot: [...state.parkingLot, note],
    parkedCount: state.parkedCount + 1,
  };
}

const isRunning = (state: FocusState) =>
  state.phase === 'focus' || state.phase === 'paused';

type Handler<T extends FocusAction['type']> = (
  state: FocusState,
  action: Extract<FocusAction, { type: T }>,
  now: number
) => FocusState;

/** One small handler per action: each guards the phases it applies in. */
const HANDLERS: { [T in FocusAction['type']]: Handler<T> } = {
  start: (s, a, now) => startBlock(s, a, now),
  pause: (s, _a, now) =>
    s.phase === 'focus' ? { ...s, phase: 'paused', pausedAt: now } : s,
  resume: (s, _a, now) => resume(s, now),
  // Ending a timed block before it runs out is early; Flow's "I'm done" is not.
  finish: (s, _a, now) =>
    isRunning(s) ? endFocus(s, now, s.plannedMs !== null) : s,
  tick: (s, _a, now) => tick(s, now),
  nextBlock: (s, _a, now) =>
    isRunning(s) ? s : { ...s, lastSession: null, ...focusFields(s, now) },
  endBreak: s => (s.phase === 'break' ? toIdle(s) : s),
  endSession: (s, _a, now) => endSession(s, now),
  dismissSummary: s => ({ ...s, lastSession: null }),
  clearSessionNotes: s =>
    s.lastSession ? { ...s, lastSession: { ...s.lastSession, notes: [] } } : s,
  addNote: (s, a, now) => addNote(s, a.text, now),
  removeNote: (s, a) => ({
    ...s,
    parkingLot: s.parkingLot.filter(n => n.id !== a.id),
  }),
  saveRecap: (s, a) =>
    a.text.trim() ? updateBlock(s, { recap: a.text.trim() }) : s,
  dismissRecap: s => updateBlock(s, { recapDismissed: true }),
};

export function reduceFocus(
  state: FocusState,
  action: FocusAction,
  now: number
): FocusState {
  // The mapped type pairs each key with its own action; TypeScript cannot
  // follow that correlation through an index, so widen once, here.
  const handler = HANDLERS[action.type] as (
    s: FocusState,
    a: FocusAction,
    now: number
  ) => FocusState;
  return handler(state, action, now);
}
